'use client';

import { QueryKeys } from '@/constants/query-keys';
import { useUpdateSharedState } from '@/hooks/state/shared-state';
import { shoppingCartApiService } from '@/services/(frontend)/checkout/shopping-cart-api.service';
import { SharedState } from '@/types/shared-state';
import { ShoppingCartReview } from '@/types/shopping-cart';
import { getShoppingCartItemCount, removeShoppingCartTicket } from '@/utils/shopping-cart/shopping-cart-helper';
import { removeShowtimeFromCart } from '@/utils/shopping-cart/shopping-cart-review';
import { PostShoppingCartConcession, PostShoppingCartTicket, PutShoppingCartConcession, PutShoppingCartTicket, ShoppingCart } from '@cinemark/checkout-api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useShoppingCart = (enabled = true, isFamilyEntertainmentCenter = false): ReturnType<typeof useQuery<ShoppingCart | null>> => {
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useQuery<ShoppingCart | null>({
    enabled,
    queryFn: async ({ signal }) => {
      const cart = await shoppingCartApiService.getCart(signal);
      updateSharedState({ cartItemCount: getShoppingCartItemCount(cart, isFamilyEntertainmentCenter), shoppingCartId: cart?.id ?? null });
      return cart;
    },
    queryKey: QueryKeys.user.shoppingCart,
  });
};

export const useShoppingCartReview = (
  initialData?: ShoppingCartReview
): ReturnType<typeof useQuery<ShoppingCartReview>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useQuery<ShoppingCartReview>({
    initialData,
    queryFn: async ({ signal }) => {
      const review = await shoppingCartApiService.getCartReview(signal);
      if (!review) {
        throw new Error('Shopping cart review was not found');
      }

      queryClient.setQueryData(QueryKeys.user.shoppingCart, review.cart);
      updateSharedState({
        cartItemCount: getShoppingCartItemCount(review.cart),
        shoppingCartId: review.cart?.id ?? null,
      });

      return review;
    },
    queryKey: QueryKeys.user.shoppingCartReview,
  });
};

type RemoveShowtimeParams = {
  showtimeId: number;
};

type RemoveShowtimeContext = {
  previousCart?: ShoppingCart | null;
  previousReview?: ShoppingCartReview;
  previousSharedState?: SharedState;
};

export const useRemoveShowtime = (): ReturnType<typeof useMutation<
  ShoppingCartReview,
  unknown,
  RemoveShowtimeParams,
  RemoveShowtimeContext
>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useMutation<ShoppingCartReview, unknown, RemoveShowtimeParams, RemoveShowtimeContext>({
    mutationFn: async ({ showtimeId }) => await shoppingCartApiService.removeShowtime(showtimeId),
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(QueryKeys.user.shoppingCartReview, context?.previousReview);
      queryClient.setQueryData(QueryKeys.user.shoppingCart, context?.previousCart);
      queryClient.setQueryData(QueryKeys.user.sharedState, context?.previousSharedState);

      if (context?.previousSharedState) {
        updateSharedState({
          cartItemCount: context.previousSharedState.cartItemCount,
          shoppingCartId: context.previousSharedState.shoppingCartId,
        });
      }
    },
    onMutate: async ({ showtimeId }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: QueryKeys.user.shoppingCartReview }),
        queryClient.cancelQueries({ queryKey: QueryKeys.user.shoppingCart }),
      ]);

      const previousReview = queryClient.getQueryData<ShoppingCartReview>(QueryKeys.user.shoppingCartReview);
      const previousCart = queryClient.getQueryData<ShoppingCart | null>(QueryKeys.user.shoppingCart);
      const previousSharedState = queryClient.getQueryData<SharedState>(QueryKeys.user.sharedState);
      const currentCart = previousReview?.cart ?? previousCart;

      if (currentCart) {
        const optimisticCart = removeShowtimeFromCart(currentCart, showtimeId);
        const optimisticReview = previousReview && {
          ...previousReview,
          cart: optimisticCart,
          showtimes: previousReview.showtimes.filter(showtime => showtime.cartShowtime.showtimeId !== showtimeId),
        };
        const cartItemCount = getShoppingCartItemCount(optimisticCart);

        queryClient.setQueryData(QueryKeys.user.shoppingCartReview, optimisticReview);
        queryClient.setQueryData(QueryKeys.user.shoppingCart, optimisticCart);
        queryClient.setQueryData<SharedState>(QueryKeys.user.sharedState, state => state && {
          ...state,
          cartItemCount,
        });
        updateSharedState({ cartItemCount });
      }

      return { previousCart, previousReview, previousSharedState };
    },
    onSuccess: (review) => {
      queryClient.setQueryData(QueryKeys.user.shoppingCartReview, review);
      queryClient.setQueryData(QueryKeys.user.shoppingCart, review.cart);
      updateSharedState({
        cartItemCount: getShoppingCartItemCount(review.cart),
        shoppingCartId: review.cart?.id ?? null,
      });
    },
  });
};

export const useClearCart = (): ReturnType<typeof useMutation<void>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useMutation({
    mutationFn: async () => {
      return await shoppingCartApiService.clearCart();
    },
    onSuccess: () => {
      queryClient.setQueryData(QueryKeys.user.shoppingCart, null);
      updateSharedState({ cartItemCount: 0, shoppingCartId: null });
    },
  });
};

export const useAddTicketToCart = (): ReturnType<typeof useMutation<ShoppingCart, unknown, PostShoppingCartTicket>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useMutation({
    mutationFn: async (ticket: PostShoppingCartTicket) => {
      return await shoppingCartApiService.addTicket(ticket);
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(QueryKeys.user.shoppingCart, cart);
      updateSharedState({ cartItemCount: getShoppingCartItemCount(cart), shoppingCartId: cart.id });
    },
  });
};

type UpdateTicketInCartParams = {
  ticket: PutShoppingCartTicket;
  ticketId: string;
};

export const useUpdateTicketInCart = (): ReturnType<typeof useMutation<ShoppingCart, unknown, UpdateTicketInCartParams>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useMutation({
    mutationFn: async ({ ticket, ticketId }: UpdateTicketInCartParams) => {
      return await shoppingCartApiService.updateTicket(ticketId, ticket);
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(QueryKeys.user.shoppingCart, cart);
      updateSharedState({ cartItemCount: getShoppingCartItemCount(cart) });
    },
  });
};

type RemoveTicketFromCartParams = {
  showtimeId: number;
  ticketId: string;
};

type RemoveTicketFromCartContext = {
  previousCart?: ShoppingCart;
};

export const useRemoveTicketFromCart = (): ReturnType<typeof useMutation<ShoppingCart, unknown, RemoveTicketFromCartParams>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useMutation({
    mutationFn: async ({ showtimeId, ticketId }: RemoveTicketFromCartParams) => {
      return await shoppingCartApiService.removeTicket(ticketId, showtimeId);
    },
    onError: (_error, _variables, context: RemoveTicketFromCartContext | undefined) => {
      if (!context?.previousCart) {
        return;
      }

      queryClient.setQueryData(QueryKeys.user.shoppingCart, context.previousCart);
      updateSharedState({
        cartItemCount: getShoppingCartItemCount(context.previousCart),
        shoppingCartId: context.previousCart.id,
      });
    },
    onMutate: async ({ showtimeId, ticketId }: RemoveTicketFromCartParams): Promise<RemoveTicketFromCartContext> => {
      await queryClient.cancelQueries({ queryKey: QueryKeys.user.shoppingCart });
      const previousCart = queryClient.getQueryData<ShoppingCart>(QueryKeys.user.shoppingCart);

      if (previousCart) {
        const optimisticCart = removeShoppingCartTicket(previousCart, showtimeId, ticketId);
        queryClient.setQueryData(QueryKeys.user.shoppingCart, optimisticCart);
        updateSharedState({ cartItemCount: getShoppingCartItemCount(optimisticCart), shoppingCartId: optimisticCart.id });
      }

      return { previousCart };
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(QueryKeys.user.shoppingCart, cart);
      updateSharedState({ cartItemCount: getShoppingCartItemCount(cart), shoppingCartId: cart.id });
    },
  });
};

export const useAddConcessionToCart = (): ReturnType<typeof useMutation<ShoppingCart, unknown, PostShoppingCartConcession>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useMutation({
    mutationFn: async (concession: PostShoppingCartConcession) => {
      return await shoppingCartApiService.addConcession(concession);
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(QueryKeys.user.shoppingCart, cart);
      updateSharedState({ cartItemCount: getShoppingCartItemCount(cart), shoppingCartId: cart.id });
    },
  });
};

type UpdateConcessionInCartParams = {
  concession: PutShoppingCartConcession;
  concessionId: string;
};

export const useUpdateConcessionInCart = (): ReturnType<typeof useMutation<ShoppingCart, unknown, UpdateConcessionInCartParams>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useMutation({
    mutationFn: async ({ concession, concessionId }: UpdateConcessionInCartParams) => {
      return await shoppingCartApiService.updateConcession(concessionId, concession);
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(QueryKeys.user.shoppingCart, cart);
      updateSharedState({ cartItemCount: getShoppingCartItemCount(cart) });
    },
  });
};

type RemoveConcessionFromCartParams = {
  concessionId: string;
  showtimeId: number;
};

export const useRemoveConcessionFromCart = (): ReturnType<typeof useMutation<ShoppingCart, unknown, RemoveConcessionFromCartParams>> => {
  const queryClient = useQueryClient();
  const { mutate: updateSharedState } = useUpdateSharedState();

  return useMutation({
    mutationFn: async ({ concessionId, showtimeId }: RemoveConcessionFromCartParams) => {
      return await shoppingCartApiService.removeConcession(concessionId, showtimeId);
    },
    onSuccess: (cart) => {
      queryClient.setQueryData(QueryKeys.user.shoppingCart, cart);
      updateSharedState({ cartItemCount: getShoppingCartItemCount(cart) });
    },
  });
};
